// ============================================================================
// More → Discovery and More → Signal (TCSD-0373 ¶8: both stay, for everyone).
// The existing components, unchanged, on their own pages under the hub now that
// Sourcing is no longer a module. Same gate as before: can_access_sourcing().
// Promoting a discovery lead goes through the one door as a Lead (item #683).
// ============================================================================

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { EmptyState, Skeleton, useToast } from '../../../shared/components';
import { useCanAccessSourcing } from '../../../shared/use-can-access-sourcing';
import { useMarkets } from '../../../shared/markets';
import { refreshReaders } from '../../../shared/save-path';
import { useDiscovery, useProviderOptions, useSourcingSignal } from '../../sourcing/hooks/use-sourcing-data';
import { DiscoveryTab } from '../../sourcing/discovery';
import { SignalTab } from '../../sourcing/signal';

function Frame({ title, children }: { title: string; children: ReactNode }) {
  const navigate = useNavigate();
  const { canAccess, loading } = useCanAccessSourcing();
  return (
    <div className="space-y-3 p-4 md:p-6">
      <button type="button" onClick={() => navigate('/people/subcontractors?view=more')} className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary">
        <ArrowLeft className="h-4 w-4" /> Subcontractors
      </button>
      <h1 className="text-xl font-semibold text-tcs-text">{title}</h1>
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : !canAccess ? (
        <EmptyState title={`${title} isn't available for your account yet`} body="Ask the COO if you think you should have access." />
      ) : (
        children
      )}
    </div>
  );
}

/** A discovery lead's market is a short name ("DFW", "St. Louis"); the sub record takes a real market. */
function marketFor(short: string | null, names: string[]): string | null {
  if (!short) return null;
  const s = short.trim().toLowerCase();
  return (
    names.find((n) => n.toLowerCase().replace(/\s*metro$/, '') === s) ??
    (s === 'dfw' ? (names.find((n) => n.startsWith('Dallas')) ?? null) : null)
  );
}

function DiscoveryBody() {
  const discovery = useDiscovery();
  const toast = useToast();
  const { markets } = useMarkets();
  const names = markets.map((m) => m.market_name);
  return (
    <DiscoveryTab
      discovery={discovery}
      onPromote={async (lead, addAnyway) => {
        await discovery.triageLead(lead, marketFor(lead.market, names), addAnyway);
        refreshReaders(['service_providers', 'sourcing_prospects']);
        toast.success(`${lead.business_name || lead.owner_name || 'Lead'} is in your Pipeline as a Lead`);
      }}
    />
  );
}

function SignalBody() {
  const signal = useSourcingSignal();
  const providers = useProviderOptions();
  return <SignalTab rows={signal.data} isLoading={signal.isLoading} providers={providers} />;
}

export function DiscoveryPage() {
  return (
    <Frame title="Discovery">
      <DiscoveryBody />
    </Frame>
  );
}

export function SignalPage() {
  return (
    <Frame title="Signal">
      <SignalBody />
    </Frame>
  );
}
